import {
  Typography,
  useMediaQuery,
  useTheme,
  Link,
  CircularProgress,
} from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { Box } from "@mui/system";
import React, { useState, useEffect } from "react";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { useAssessment } from "../../providers/AssessmentProvider";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import Chip from "@mui/material/Chip";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Button from "@mui/material/Button";
import { getAssessmentResultStyle } from "../../components/customstyles/AssessmentResultStyle";
import { usePatientAndAssessmentDataHelper } from "../../providers/PatientAndAssessmentDataHelperProvider";
import { usePatient } from "../../providers/PatientProvider";

import moment from "moment";
import AssessmentGraph from "./AssessmentGraph";
import ErrorDialog from "../../components/ErrorDialog";

const useStyles = (theme) => {
  return getAssessmentResultStyle(theme);
};

function AssessmentResult() {
  const { getPatientData, getAssessmentDetails } =
    usePatientAndAssessmentDataHelper();
  const theme = useTheme();
  const styles = useStyles(theme);
  const history = useHistory();
  const { patientData } = usePatient();
  const [totalScore, setTotalScore] = useState(0);
  const [totalSectionScores, setTotalSectionScores] = useState(0);
  const [totalAssessments, setTotalAssessments] = useState(0);
  const [lastAssessmentDate, setLastAssessmentDate] = useState("");
  const [firstAssessmentDate, setFirstAssessmentDate] = useState("");
  const [assessmentDate, setAssessmentDate] = useState("");
  let [patientId, setPatientId] = useState(null);
  let { assessment } = useAssessment();
  const isMobile = useMediaQuery("(max-width: 900px)");

  const location = useLocation();
  const getPatientAndAssessmentId = () => {
    let patientId;
    let pathname = location.pathname.substring(1);
    pathname = pathname.split("/");
    patientId = pathname[1];
    let assessmentIdIndex = pathname.indexOf("result") + 1;
    let assessmentId = pathname[assessmentIdIndex];
    return { patientId, assessmentId };
  };
  const handlePrint = () => {
    window.print();
  };

  const handleDate = (dob) => {
    const format = "YYYY-MM-DD";
    let today = new Date();
    today = moment(today).format(format);
    const dobFormatted = moment(dob).format(format);
    if (today !== dobFormatted) {
      return moment(dob).format("MMM DD, YYYY");
    } else {
      return "Today";
    }
  };
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    let { patientId, assessmentId } = getPatientAndAssessmentId();
    setPatientId(patientId);
    getPatientData(patientId);
    const handleAssessments = async (assessmentId) => {
      if (assessmentId) {
        let returnedData = await getAssessmentDetails(patientId, assessmentId);
        if (returnedData.status) {
          setTotalScore(returnedData.data.score);
          setTotalSectionScores(returnedData.data.sectionScores);
          setFirstAssessmentDate(
            handleDate(returnedData.data.firstAssessmentDate)
          );
          setAssessmentDate(
            moment(returnedData.data.assessmentDate).format("MMM DD, YYYY")
          );
          setLastAssessmentDate(
            handleDate(returnedData.data.lastAssessmentDate)
          );
          setTotalAssessments(returnedData.data.totalAssessments);
        } else {
          setShowErrorDialog(true);
        }
      }
    };
    handleAssessments(assessmentId);
    styles.injectPrintStyles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleBackToPatientClick = () => {
    setShowErrorDialog(false);
    history.push({
      pathname: `/patients/${patientId}`,
    });
  };

  return (
    <React.Fragment>
      {showErrorDialog && (
        <ErrorDialog HeaderText={"Something went wrong"} dismiss={""}>
          <Button
            primary
            sx={theme.customErrorDialog}
            onClick={handleBackToPatientClick}
          >
            Take Me To Patient Page
          </Button>
        </ErrorDialog>
      )}
      <Box
        sx={{
          display: "none",
          displayPrint: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "left",
          padding: "0 34px",
          width: "100%",
        }}
      >
        <Typography variant="h5" fontSize={20} fontWeight={500}>
          Assessment Results
        </Typography>
        <Typography fontSize={20} fontWeight={500}>
          {moment(assessmentDate).format("MMM DD, YYYY")}
        </Typography>
      </Box>
      {patientData ? (
        <div
          style={{
            minHeight: "65vh",
            maxWidth: "920px",
            width: "100%",
            margin: "0 auto",
            padding: "30px",
            "@media print": { padding: "0 !important", margin: "0 !important" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              margin: "10px 0",
              displayPrint: "none",
            }}
          >
            <Link href="/patients" underline="none">
              <Typography
                variant="body1"
                sx={{ color: "#0D47A1", textDecoration: "underline" }}
              >
                Patients List
              </Typography>
            </Link>
            <ChevronRight />
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() => {
                history.push({
                  pathname: `/patients/${patientData.patientId}/1`,
                });
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "#0D47A1", textDecoration: "underline" }}
              >
                {patientData && patientData.patientName}
              </Typography>
            </Link>
            <ChevronRight />
            <Typography variant="body1">{assessmentDate} Results</Typography>
          </Box>
          <Box sx={{ textAlign: "center", gap: 32 }}>
            <Box
              sx={{
                display: "none",
                displayPrint: "block",
                textAlign: "left",
                margin: "4px",
              }}
            >
              <Typography variant="h5" fontSize={32} fontWeight={400}>
                {patientData.patientName}
              </Typography>
              <Typography fontSize={24} fontWeight={400}>
                Date of Birth:{" "}
                {moment(patientData.birthdate).format("MMM DD, YYYY")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                displayPrint: "none",
              }}
            >
              <Typography variant="h6" fontSize={32} fontWeight={400}>
                Assessment Results
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  padding: "8px 12px",
                }}
                endIcon={<SaveAltIcon />}
                onClick={handlePrint}
              >
                Download PDF
              </Button>
            </Box>

            <Box sx={styles.briefDataStyle(isMobile)}>
              <Box
                sx={{
                  ...styles.briefDataBoxStyle(isMobile),
                  "@media print": { borderRight: "none" },
                }}
              >
                <Typography
                  variant="h6"
                  fontSize={isMobile ? "16px" : "20px"}
                  sx={{ "@media print": { fontSize: "32px" } }}
                >
                  {totalScore}
                </Typography>
                <Typography
                  color={"#00000099"}
                  variant="p"
                  displayPrint="none"
                  fontSize={isMobile ? "12px" : "14px"}
                >
                  Score
                </Typography>
                <Typography
                  color={"#00000099"}
                  variant="p"
                  display="none"
                  displayPrint="block"
                  fontSize={isMobile ? "20px" : "24px"}
                >
                  Score
                </Typography>
              </Box>
              <Box
                sx={{
                  ...styles.briefDataBoxStyle(isMobile),
                  displayPrint: "none",
                }}
              >
                <Typography variant="h6" fontSize={isMobile ? "16px" : "20px"}>
                  {totalAssessments}
                </Typography>
                <Typography
                  color={"#00000099"}
                  variant="p"
                  fontSize={isMobile ? "12px" : "14px"}
                >
                  Assessments taken
                </Typography>
              </Box>
              <Box
                sx={{
                  ...styles.briefDataBoxStyle(isMobile),
                  displayPrint: "none",
                }}
              >
                <Typography variant="h6" fontSize={isMobile ? "16px" : "20px"}>
                  {firstAssessmentDate}
                </Typography>
                <Typography
                  color={"#00000099"}
                  variant="p"
                  fontSize={isMobile ? "12px" : "14px"}
                >
                  First assessment
                </Typography>
              </Box>
              <Box
                sx={{
                  ...styles.briefDataBoxStyle(isMobile, true),
                  displayPrint: "none",
                }}
              >
                <Typography variant="h6" fontSize={isMobile ? "16px" : "20px"}>
                  {lastAssessmentDate}
                </Typography>
                <Typography
                  color={"#00000099"}
                  variant="p"
                  fontSize={isMobile ? "12px" : "14px"}
                >
                  Last assessment
                </Typography>
              </Box>
            </Box>

            <Box sx={{ displayPrint: "none" }}>
              <AssessmentGraph />
            </Box>

            <Box
              sx={{
                ...styles.warningBoxStyle(),
                displayPrint: "none",
              }}
            >
              <WarningRoundedIcon sx={{ color: "#FAB509", fontSize: "30px" }} />
              <Box
                sx={{ textAlign: "left", padding: "4px 0px", fontSize: "14px" }}
              >
                <Typography sx={{ mb: "15px" }} fontSize={"14px"}>
                  A minimum score of 2 on any AIMS item in the body area
                  sections means that TD movements have been clearly identified
                  and documented, requiring additional assessment of impact.
                </Typography>
                <Typography fontSize={"14px"}>
                  If AIMS reveals TD symptoms, refer to ICD-10-CM: G24.01 for TD
                  from the DSM-5 for further evaluation.
                </Typography>
              </Box>
            </Box>
            <Box>
              <Box
                sx={{ marginY: "10px", paddingY: "10px", textAlign: "left" }}
              >
                <Typography variant="h5" sx={{ displayPrint: "none" }}>
                  Score Details
                </Typography>
              </Box>
              <div className="FSUnmask">
                <Box
                  sx={{
                    padding: "5px",
                    backgroundColor: "white",
                    border: "1px solid #0304041A",
                    borderRadius: "8px",
                    "@media print": { padding: "10px !important" },
                  }}
                >
                  <Typography
                    variant="h5"
                    textAlign={"left"}
                    sx={{
                      display: "none",
                      displayPrint: "block",
                      padding: "10px",
                    }}
                  >
                    Details
                  </Typography>
                  {Object.values(assessment).map(
                    (outerSection, outerSectionIndex) => {
                      return outerSection.sectionAssessment.map(
                        (subSection, subSectionIndex) => {
                          const isLastSection =
                            outerSectionIndex ===
                            Object.values(assessment).length - 1;
                          const isLastSubSection =
                            subSectionIndex ===
                            outerSection.sectionAssessment.length - 1;
                          return (
                            <Box
                              sx={{
                                pb: "15px",
                                mb: "15px",
                                "@media print": {
                                  borderBottom:
                                    !isLastSection || !isLastSubSection
                                      ? "1px solid #0304041A"
                                      : "",
                                },
                              }}
                            >
                              <Box sx={styles.sectionStyle()}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <AssignmentRoundedIcon
                                    sx={{
                                      color: "#0D47A1",
                                      margin: "8px",
                                      fontSize: "30px",
                                      displayPrint: "none",
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        width: "100%",
                                        textAlign: "left",
                                        padding: "8px",
                                        // "@media print": {
                                        //   padding: "0 !important",
                                        //   ...(subSection.name === "Truncal" && {
                                        //     pageBreakBefore: "always",
                                        //     breakBefore: "page", // for modern browsers
                                        //   }),
                                        // },
                                      }}
                                    >
                                      {subSection.name}
                                    </Typography>
                                    {subSection.questions[0]["description"] && (
                                      <Typography
                                        variant="p"
                                        sx={{
                                          display: "table-cell",
                                          width: "100%",
                                          textAlign: "left",
                                          padding: "8px",
                                          "@media print": {
                                            padding: "0 !important",
                                          },
                                        }}
                                      >
                                        {subSection.questions[0]["description"]}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                {outerSection.key === "bodyAreas" && (
                                  <Chip
                                    label={`Score: ${
                                      totalSectionScores[subSection.key]
                                    }`}
                                    sx={{
                                      backgroundColor: "#0D47A1",
                                      color: "white",
                                    }}
                                  />
                                )}
                              </Box>
                              <Box
                                sx={{
                                  paddingX: "40px",
                                  "@media print": {
                                    paddingX: "20px !important",
                                  },
                                }}
                              >
                                {subSection.questions.map(
                                  (question, questionIndex) => {
                                    return (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          padding: "10px",
                                          borderBottom:
                                            questionIndex <
                                            subSection.questions.length - 1
                                              ? "1px solid #0304041A"
                                              : "",
                                          color: "#263238",
                                        }}
                                      >
                                        <Typography fontSize="14px">
                                          {question.title}
                                        </Typography>
                                        <Typography fontSize="14px">
                                          {question.selectedValue !== null
                                            ? question.selectedValue
                                            : "Not Answered"}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                )}
                              </Box>
                              {subSection.notes && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    padding: "0px 24px",
                                    backgroundColor: "#F7F9FA",
                                    alignItems: "center",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <EditNoteRoundedIcon
                                    sx={{
                                      margin: "8px",
                                      fontSize: "30px",
                                      displayPrint: "none",
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      textAlign: "left",
                                      width: "90%",
                                      padding: "8px",
                                      color: "#546E7A",
                                      fontSize: "14px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        color: "#546E7A",
                                        fontSize: "14px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Notes:
                                    </Typography>
                                    <Typography
                                      className="FSMask"
                                      sx={{
                                        flexWrap: "wrap",
                                        wordWrap: "break-word",
                                      }}
                                    >
                                      {subSection.notes}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          );
                        }
                      );
                    }
                  )}
                </Box>
              </div>
            </Box>
            <Button
              variant="contained"
              sx={{
                ...styles.buttonStyle(isMobile),
                mt: "20px",
                displayPrint: "none",
              }}
              onClick={() => {
                history.push({
                  pathname: `/patients/${patientId}/1`,
                });
              }}
            >
              Back To Patient Page
            </Button>
          </Box>
        </div>
      ) : (
        <CircularProgress
          sx={{ position: "absolute", top: "50%", left: "50%" }}
        />
      )}
    </React.Fragment>
  );
}

export default AssessmentResult;
